if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.abortController = null;
    }

    connectedCallback() {
      if (!this.dataset.nextUrl || this.observer) return;

      this.nextLink = this.querySelector('.infinite-scroll__next');
      this.status = this.querySelector('.infinite-scroll__status');

      if (!('IntersectionObserver' in window)) return;

      this.classList.add('infinite-scroll--enabled');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.abortController?.abort();
    }

    async loadNextPage() {
      if (this.loading || !this.dataset.nextUrl) return;

      this.loading = true;
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = this.nextLink?.textContent.trim() || '';

      this.abortController = new AbortController();

      try {
        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.toString(), { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Unable to load products (${response.status})`);

        const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
        const currentGrid = document.getElementById('product-grid');
        const nextGrid = documentFromResponse.getElementById('product-grid');
        const nextLoader = documentFromResponse.querySelector('infinite-scroll');

        if (!currentGrid || !nextGrid) throw new Error('Product grid not found in response');

        const products = Array.from(nextGrid.children);
        products.forEach((product) => {
          product.classList.add('scroll-trigger--cancel');
          currentGrid.appendChild(product);
        });
        document.dispatchEvent(new Event('collection:products-added'));

        if (typeof window.initializeScrollAnimationTrigger === 'function') {
          window.initializeScrollAnimationTrigger();
        }

        if (nextLoader?.dataset.nextUrl) {
          this.dataset.nextUrl = nextLoader.dataset.nextUrl;
          if (this.nextLink) this.nextLink.href = nextLoader.dataset.nextUrl;
          this.loading = false;
          this.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
          if (this.status) this.status.textContent = '';

          // IntersectionObserver may not fire again if the loader stayed in view.
          requestAnimationFrame(() => {
            if (this.getBoundingClientRect().top < window.innerHeight + 600) this.loadNextPage();
          });
        } else {
          this.finish();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.loading = false;
        this.classList.remove('is-loading', 'infinite-scroll--enabled');
        this.removeAttribute('aria-busy');
        if (this.status) this.status.textContent = '';
        this.observer?.disconnect();
      }
    }

    finish() {
      this.observer?.disconnect();
      this.dataset.nextUrl = '';
      this.remove();
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
